import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { SnackbarProvider } from 'notistack';
import './i18n/i18n';
import Box from '@mui/material/Box';
import { queryClient } from './lib/queryClient';
import { UIContextProvider } from './context/UIContext';
import ThemeContext from './context/ThemeContext';
import { ApiContextProvider } from './context/ApiContext';
import AppSidebar from './components/AppSidebar';
import MainContainer from './components/MainContainer';
import AdminMobileBottomNav from './components/AdminMobileBottomNav';
import NotFound from './screens/NotFound';
import BookingPage from './screens/BookingPage';
import PublicWaitlistPage from './screens/PublicWaitlistPage';
import BookingSuccess from './screens/BookingSuccess';
import BookingCancel from './screens/BookingCancel';
import ManageBooking from './screens/ManageBooking';
import AdminDashboard from './screens/AdminDashboard';
import AdminCalendar from './screens/AdminCalendar';
import AdminServices from './screens/AdminServices';
import AdminStaff from './screens/AdminStaff';
import AdminCustomers from './screens/AdminCustomers';
import AdminSettings from './screens/AdminSettings';
import AdminBookings from './screens/AdminBookings';
import AdminReports from './screens/AdminReports';
import AdminResources from './screens/AdminResources';
import AdminCustomerDetail from './screens/AdminCustomerDetail';
import AdminBusinessGate from './components/AdminBusinessGate';
import AccountLogin from './screens/AccountLogin';
import AccountPortal from './screens/AccountPortal';
import LoggedOut from './screens/loggedOut/LoggedOut';
import { getBaseDomain } from './helpers/helpers';
import { UserContextProvider } from './context/UserContext';

const drawerWidth = 256;

function cookieDefaultSetOptions(): { domain?: string } {
  const {hostname} = new URL(window.location.href);
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return {};
  }
  return { domain: `.${getBaseDomain(window.location.href)}` };
}

function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden' }}>
      <AppSidebar
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        isMdUp={isMdUp}
      />
      <MainContainer isMobile={!isMdUp} onMenuClick={() => setMobileOpen(true)}>
        <Outlet />
      </MainContainer>
      {!isMdUp && <AdminMobileBottomNav />}
    </Box>
  );
}

function AdminSection() {
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route element={<AdminBusinessGate />}>
          <Route path="login" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="offerings" element={<AdminServices />} />
          <Route path="services" element={<Navigate to="/admin/offerings" replace />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="customers/:id" element={<AdminCustomerDetail />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="resources" element={<AdminResources />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="branding" element={<Navigate to="/admin/settings?tab=booking-page" replace />} />
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/book/:slug/waitlist" element={<PublicWaitlistPage />} />
      <Route path="/book/:slug" element={<BookingPage />} />
      <Route path="/" element={<BookingPage />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/booking/cancel" element={<BookingCancel />} />
      <Route path="/manage/:token" element={<ManageBooking />} />
      <Route path="/account/login" element={<AccountLogin />} />
      <Route path="/account" element={<AccountPortal />} />
      <Route path="/logged-out" element={<LoggedOut />} />
      <Route
        path="/admin/*"
        element={
          <ApiContextProvider>
            <UserContextProvider>
              <AdminSection />
            </UserContextProvider>
          </ApiContextProvider>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <CookiesProvider defaultSetOptions={cookieDefaultSetOptions()}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <UIContextProvider>
            <ThemeContext>
              <SnackbarProvider maxSnack={4} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <AppRoutes />
              </SnackbarProvider>
            </ThemeContext>
          </UIContextProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </CookiesProvider>
  );
}
